/**
 * One value per multi-output series type, keyed by SERIES TYPE (not by interface name —
 * `MACD` produces `IndicatorMacdValue`).
 *
 * The values are dummies; only the field NAMES carry meaning. They are typed, so they
 * cannot drift from the shapes they mirror.
 */
export const INDICATOR_VALUE_SHAPES = {
    MACD: { macd: 0, signal: 0, divergence: 0 },
    BollingerBand: { top: 0, middle: 0, bottom: 0 },
    Whale: { whaleBuyVol: 0, whaleSellVol: 0 },
    KDJ: { k: 0, d: 0, j: 0 },
    DMI: { plusDI: 0, minusDI: 0, adx: 0 },
    BRAR: { ar: 0, br: 0 },
    MTM: { mtm: 0, signal: 0 },
    EMV: { emv: 0, signal: 0 },
    TRIX: { trix: 0, signal: 0 },
    DMA: { ddd: 0, ama: 0 },
    PSY: { psy: 0, signal: 0 },
    CR: { cr: 0, ma1: 0, ma2: 0, ma3: 0, ma4: 0 },
    // The shape says bullPower/bearPower. The hand-written map said bull/bear.
    StrengthElder: { bullPower: 0, bearPower: 0 },
    Ichimoku: { tenkan: 0, kijun: 0, senkouA: 0, senkouB: 0, chikou: 0 },
    Supertrend: { supertrend: 0, direction: 0, upperBand: 0, lowerBand: 0 },
    Keltner: { upper: 0, middle: 0, lower: 0 },
    Donchian: { upper: 0, middle: 0, lower: 0 },
    VWAP: { vwap: 0, upper1: 0, lower1: 0, upper2: 0, lower2: 0, upper3: 0, lower3: 0 },
    AnchoredVWAP: { vwap: 0, upper1: 0, lower1: 0, upper2: 0, lower2: 0, upper3: 0, lower3: 0 },
    Alligator: { jaw: 0, teeth: 0, lips: 0 },
    Gator: { upper: 0, lower: 0 },
    Fractals: { bearish: 0, bullish: 0 },
    // `color` is a string, so the numeric derivation excludes it without help.
    AC: { ac: 0, color: "" },
    PivotStandard: { pp: 0, r1: 0, s1: 0, r2: 0, s2: 0, r3: 0, s3: 0 },
    PivotHighLow: { pp: 0, r1: 0, s1: 0, r2: 0, s2: 0, r3: 0, s3: 0 },
    // `divergence` deliberately omitted: optional, and absent from the fallback branch.
    DeltaProfile: { cumulativeDelta: 0, barDelta: 0, deltaSMA: 0 },
    // `isPivot` is a boolean, so the numeric derivation excludes it without help.
    WyckoffWave: { direction: 0, zigzag: 0, waveVolume: 0, waveDuration: 0, isPivot: false },
    // The array members are declared but left out by the numeric derivation; see the header.
    MarketProfile: {
        tpoBlocks: [],
        poc: 0,
        valueAreaHigh: 0,
        valueAreaLow: 0,
        initialBalanceHigh: 0,
        initialBalanceLow: 0,
        profileHigh: 0,
        profileLow: 0,
    },
    VolumeProfile: { priceLevels: [], poc: 0, valueAreaHigh: 0, valueAreaLow: 0, highVolumeNodes: [], lowVolumeNodes: [] },
    Footprint: { timestamp: 0, open: 0, high: 0, low: 0, close: 0, priceLevels: [] },
};
/**
 * The numeric field names of every declared shape.
 *
 * Derived, never hand-written — see `deriveOutputKeys` in `outputKeys.ts`, which filters
 * to numbers and is what keeps arrays and booleans out of the sub-key list.
 */
export function indicatorValueShapeKeys() {
    const out = {};
    for (const [type, sample] of Object.entries(INDICATOR_VALUE_SHAPES)) {
        out[type] = Object.keys(sample).filter((k) => typeof sample[k] === "number");
    }
    return out;
}
